import { z } from "zod";

/**
 * How a rubric came to exist. Mirrors the `rubric_origin` pgEnum exactly
 * (source of truth: `@kuzco/db` RubricsTable `origin`).
 */
const rubricOriginSchema = z.enum(["analysis", "system", "user"]);

/**
 * One immutable version of a rubric (`RubricVersionsTable`). `scoreMin`/
 * `scoreMax` are Postgres `numeric(3,1)` columns, so Drizzle returns them as
 * STRINGS (e.g. "1.0"), not numbers. `createdAt` is a `Date` object — the
 * `.output()` schema validates the resolver's raw return before serialization.
 */
export const rubricVersionSchema = z.object({
  id: z.string(),
  rubricId: z.string(),
  version: z.number(),
  template: z.string(),
  scoreMin: z.string(),
  scoreMax: z.string(),
  analysisRunId: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.date(),
});

/**
 * The stored rubric row plus the resolved judge model/provider, shared by the
 * detail and list projections. `origin` mirrors the `rubric_origin` pgEnum; the
 * `judge*` fields and `projectId`/`analysisRunId`/`archivedAt` are null when
 * unset. Dates are `Date` objects.
 */
const rubricBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  teamId: z.string(),
  projectId: z.string().nullable(),
  origin: rubricOriginSchema,
  analysisRunId: z.string().nullable(),
  selectedRouteIds: z.array(z.string()),
  judgeModelId: z.string().nullable(),
  // Typed `Provider | null` upstream, but `normalizeEvalProviderName` casts an
  // arbitrary route `providerName` (`as Provider`), so a BYOK provider slug
  // outside the enum reaches here at runtime. A strict enum would 500 the
  // `.output()` validation — keep it a plain nullable string (same reason the
  // eval `provider` field was widened).
  judgeProvider: z.string().nullable(),
  judgeCanonicalModelAlias: z.string().nullable(),
  judgeRouteId: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  archivedAt: z.date().nullable(),
});

/**
 * The raw stored rubric row (`RubricsTable` select), without the read-path
 * synthesized `judgeModelId`/`judgeProvider`. This is what the rubric mutation
 * resolvers return (after `exposeRubricRouteIds` masking) — used by
 * `rubric.updateName`/`updateModels`/`updateJudgeModel`.
 */
export const rubricRowSchema = rubricBaseSchema.omit({
  judgeModelId: true,
  judgeProvider: true,
});

/** `rubric.generateDraft` — a generated judge template + sampled row count. */
export const rubricDraftSchema = z.object({
  template: z.string(),
  sampleCount: z.number(),
});

/**
 * A rubric with its current published version (`rubric.get`).
 */
export const rubricWithLatestVersionSchema = rubricBaseSchema.extend({
  latestVersion: rubricVersionSchema,
});

/**
 * A rubric pinned to a specific requested version (`rubric.getVersion`).
 */
export const rubricWithVersionSchema = rubricBaseSchema.extend({
  version: rubricVersionSchema,
});

/**
 * One row of the per-team/project rubric list (`rubric.list`): the rubric with
 * its latest version and a total version count.
 */
export const rubricListItemSchema = rubricBaseSchema.extend({
  latestVersion: rubricVersionSchema,
  versionCount: z.number(),
});
